// ---------------------------------------------------------------------------
// useAgent — fetch a single agent's working copy by name. Stays idle (empty,
// non-loading) until both `workspaceName` and `agentName` are non-null/non-empty
// so a consumer can render a placeholder without a fake loading flash. Built on
// the shared `useAsyncData` primitive for race-safe loading + refresh.
// ---------------------------------------------------------------------------

import { useMemo } from 'react';

import { useHttpClient } from '../../api/HttpClientContext.js';
import { AgentsService } from '../../services/agentsService.js';
import type { Agent } from '../../types/agent.js';
import { useAsyncData } from '../useAsyncData/useAsyncData.js';

import type { UseAgentParams, UseAgentResult } from './useAgent.interface.js';

export function useAgent(params: UseAgentParams): UseAgentResult {
  const { workspaceName, agentName } = params;
  const client = useHttpClient();
  const service = useMemo(() => new AgentsService(client), [client]);

  const fetcher = useMemo<
    ((signal: AbortSignal) => Promise<Agent>) | null
  >(() => {
    if (
      workspaceName === null ||
      agentName === null ||
      agentName.length === 0
    ) {
      return null;
    }
    return (signal) => service.get(workspaceName, agentName, { signal });
  }, [service, workspaceName, agentName]);

  const { data, isLoading, error, refresh } = useAsyncData<Agent>(fetcher, {
    errorMessage: 'Failed to load agent.',
  });

  return useMemo<UseAgentResult>(
    () => ({ agent: data, isLoading, error, refresh }),
    [data, isLoading, error, refresh],
  );
}
